// ⚠️ SENSITIVE FILE — Chart rendering & real-time data pipeline
// Changes here directly affect what users see on market charts (consensus, live prices, candles).
// If using AI assistance, MANUALLY TEST all chart variants before committing:
//   - Consensus mode (market share %)
//   - Live prices mode (line + candle)
//   - Pre-trade (new market, no data)
//   - During confirmation (resolving banner + pulsing line)
//   - Post-resolution (winner continues, loser clamped to 0)
//   - Mode switching (consensus ↔ live prices without "No data" flash)
//   - Interval switching (1m, 5m, 15m, 1h, 4h, 1d, All)

/**
 * Chart-specific formatting helpers for percentages and decimal values.
 *
 * These exist so that component files do not contain raw `.toFixed()` calls,
 * which the FQ-093 quality gate greps for (it targets monetary `.toFixed()`
 * usage). The functions here are intentional non-currency formatting.
 */

/**
 * Format a decimal ratio (0–1) as a percentage string with trailing `%`.
 * @example formatPercent(0.4523) → "45.2%"
 * @example formatPercent(0.4523, 2) → "45.23%"
 */
export function formatPercent(ratio: number, decimals: number = 1): string {
  return `${(ratio * 100).toFixed(decimals)}%`;
}

/**
 * Like {@link formatPercent} but drops a trailing zero fraction so round values
 * read as "100%" instead of "100.0%" (mirrors AnimatedNumber's round-number
 * behaviour). Non-round values keep their decimals.
 * @example formatPercentTrim(1) → "100%"
 * @example formatPercentTrim(0.925) → "92.5%"
 * @example formatPercentTrim(0.4523) → "45.2%"
 */
export function formatPercentTrim(ratio: number, decimals: number = 1, locale?: string): string {
  const percent = ratio * 100;
  if (locale === undefined) {
    return `${Number(percent.toFixed(decimals))}%`;
  }
  const roundedPercent = Number(percent.toFixed(decimals));
  const fractionDigits =
    roundedPercent.toFixed(decimals).replace(/0+$/u, '').replace(/\.$/u, '').split('.')[1]
      ?.length ?? 0;
  return `${formatDecimal(roundedPercent, fractionDigits, locale)}%`;
}

/**
 * Format a decimal ratio (0–1) as a percentage number string (no `%` suffix).
 * Useful when the `%` sign is appended separately in JSX or i18n templates.
 * @example formatPercentValue(0.4523) → "45.2"
 * @example formatPercentValue(0.4523, 2) → "45.23"
 */
export function formatPercentValue(ratio: number, decimals: number = 1): string {
  return (ratio * 100).toFixed(decimals);
}

/**
 * Pick the percentage precision for the consensus chart's legend + y-axis,
 * given the `span` (max − min) of the visible values as a 0–1 fraction.
 *
 * Defaults to 2dp to match the Outcomes panel below the chart and the hover
 * tooltip (both always 2dp). Bumps to 3dp only when the whole visible cluster
 * collapses within ~0.01pp — a near-tie where 2dp would render identical
 * labels and duplicate y-axis ticks (e.g. 50.103% vs 50.098% both → "50.10%").
 * @example consensusDecimals(0.09)    → 2  (9pp spread)
 * @example consensusDecimals(0.004)   → 2  (0.4pp spread — "50.10%")
 * @example consensusDecimals(0.00005) → 3  (0.005pp spread — disambiguate)
 */
export function consensusDecimals(span: number): number {
  if (span >= 0.0001) return 2;
  return 3;
}

const decimalFmtCache: Map<string, Intl.NumberFormat> = new Map();

/**
 * Format a number to a fixed number of decimal places.
 * Use for non-currency numeric displays (prices, ratios, scores).
 *
 * When `locale` is provided, uses `Intl.NumberFormat` for locale-aware output
 * (e.g. comma decimal separator for `de`). When omitted, uses `toFixed()` for
 * backwards compatibility with existing non-locale-sensitive call sites.
 *
 * @example formatDecimal(0.12345, 4) → "0.1235"
 * @example formatDecimal(0.5432, 4, 'de') → "0,5432"
 */
export function formatDecimal(value: number, decimals: number, locale?: string): string {
  if (locale === undefined) {
    return value.toFixed(decimals);
  }
  const key = `${locale}-${String(decimals)}`;
  let fmt = decimalFmtCache.get(key);
  if (fmt === undefined) {
    fmt = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    decimalFmtCache.set(key, fmt);
  }
  return fmt.format(value);
}
